import type { QueryOptions } from 'types'
import { RequestError } from 'utils/RequestError'

const defaultQueryOptions: QueryOptions = {
  enabled: true,
  instanceKey: 'default',
  refetchOnWindowFocus: false,
  retry: false,
}

// Kept low: combined with the request timeout, each extra retry adds up to a
// full timeout's worth of spinner before the error finally surfaces. See PX-3333.
const MAX_QUERY_RETRIES = 2
// Fan-out queries re-run *every* sub-request on each retry, so they retry less
// to avoid amplifying load on an already-degraded backend.
const MAX_FANOUT_QUERY_RETRIES = 1
const MAX_RETRY_DELAY_MS = 30_000

/**
 * Decides whether a failed request is worth retrying. Retries transient
 * failures — network errors, request timeouts, 5xx responses and 429 rate
 * limits — but never terminal client errors (4xx), which will fail again.
 */
const isRetryableError = (error: unknown): boolean => {
  // Request timeout: the HTTP client aborts the fetch, which rejects with an
  // AbortError. This assumes aborts originate from our own timeout — no
  // resilient query passes a caller AbortSignal today. If one ever does, a
  // user-initiated cancellation would be retried here and must be excluded
  // first (see fetchWithTimeout, which already distinguishes the two).
  if (error instanceof Error && error.name === 'AbortError') return true

  // Network failure: fetch rejects with a TypeError (e.g. connection dropped).
  if (error instanceof TypeError) return true

  if (error instanceof RequestError) {
    // A status-less RequestError means the failure happened before a response
    // arrived, so treat it as transient.
    if (error.status === undefined) return true

    return error.status >= 500 || error.status === 429
  }

  return false
}

const makeRetryRequest =
  (maxRetries: number) =>
  (failureCount: number, error: unknown): boolean =>
    failureCount < maxRetries && isRetryableError(error)

const retryRequest = makeRetryRequest(MAX_QUERY_RETRIES)

const retryDelay = (attemptIndex: number): number => Math.min(1_000 * 2 ** attemptIndex, MAX_RETRY_DELAY_MS)

/**
 * Query options for reads whose failure should not immediately surface as a
 * hard error. Restores exponential-backoff retries (disabled globally via
 * {@link defaultQueryOptions}) so transient backend degradation self-heals
 * instead of bricking the view. See PX-3333.
 */
const resilientQueryOptions: QueryOptions = {
  ...defaultQueryOptions,
  retry: retryRequest,
  retryDelay,
}

/**
 * Like {@link resilientQueryOptions} but for a query that fans out to many
 * sub-requests in a single query function (e.g. a Promise.all): retries are
 * capped lower because each retry re-issues the whole fan-out.
 */
const resilientFanOutQueryOptions: QueryOptions = {
  ...defaultQueryOptions,
  retry: makeRetryRequest(MAX_FANOUT_QUERY_RETRIES),
  retryDelay,
}

export {
  MAX_FANOUT_QUERY_RETRIES,
  MAX_QUERY_RETRIES,
  defaultQueryOptions,
  isRetryableError,
  resilientFanOutQueryOptions,
  resilientQueryOptions,
  retryDelay,
  retryRequest,
}
